import { PageContainer } from "@/components/common/page-container";
import { AlbumAnalytics } from "@/routes/custom-music/dsp-analytics/components/album-analytics";
import { ArtistAnalytics } from "@/routes/custom-music/dsp-analytics/components/artist-analytics";
import { CountryAnalytics } from "@/routes/custom-music/dsp-analytics/components/country-analytics";
import { MonthRangeSelect } from "@/routes/custom-music/dsp-analytics/components/month-range-select";
import { OverviewAnalytics } from "@/routes/custom-music/dsp-analytics/components/overview-analytics";
import { PlatformAnalytics } from "@/routes/custom-music/dsp-analytics/components/platform-analytics";
import { TrackAnalytics } from "@/routes/custom-music/dsp-analytics/components/track-analytics";
import { AnalyticsMonthProvider } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import {
  Loading,
  Typography,
  UnderlineTabs,
  UnderlineTabsContent,
  UnderlineTabsList,
  UnderlineTabsTrigger,
} from "@backpac/monica";
import { Suspense } from "react";
import { useSearchParams } from "react-router";
import type { ValueOf } from "type-fest";

const DSPAnalyticsTabs = {
  Overview: {
    label: "Overview",
    value: "overview",
  },
  Platform: {
    label: "Platform",
    value: "platform",
  },
  Album: {
    label: "Album",
    value: "album",
  },
  Track: {
    label: "Track",
    value: "track",
  },
  Artist: {
    label: "Artist",
    value: "artist",
  },
  Country: {
    label: "Country",
    value: "country",
  },
} as const;

function useDSPAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") ?? DSPAnalyticsTabs.Overview.value) as ValueOf<typeof DSPAnalyticsTabs>["value"];

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return {
    tab,
    handleTabChange,
  };
}

export function DSPAnalyticsPage() {
  const { tab, handleTabChange } = useDSPAnalytics();

  return (
    <AnalyticsMonthProvider>
      <PageContainer nav={<PageContainer.Navigation title="DSP Analytics" />}>
        <div className="flex items-center justify-between mb-6">
          <Typography asChild size="xl" color="textPrimary" font="unbounded">
            <h1>DSP Analytics</h1>
          </Typography>
          <MonthRangeSelect />
        </div>
        <UnderlineTabs value={tab} onValueChange={handleTabChange}>
          <UnderlineTabsList>
            <UnderlineTabsTrigger value={DSPAnalyticsTabs.Overview.value}>
              {DSPAnalyticsTabs.Overview.label}
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger value={DSPAnalyticsTabs.Platform.value}>
              {DSPAnalyticsTabs.Platform.label}
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger value={DSPAnalyticsTabs.Album.value}>
              {DSPAnalyticsTabs.Album.label}
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger value={DSPAnalyticsTabs.Track.value}>
              {DSPAnalyticsTabs.Track.label}
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger value={DSPAnalyticsTabs.Artist.value}>
              {DSPAnalyticsTabs.Artist.label}
            </UnderlineTabsTrigger>
            <UnderlineTabsTrigger value={DSPAnalyticsTabs.Country.value}>
              {DSPAnalyticsTabs.Country.label}
            </UnderlineTabsTrigger>
          </UnderlineTabsList>
          <Suspense fallback={<DSPAnalyticsPage.ContentPending />}>
            <UnderlineTabsContent value={DSPAnalyticsTabs.Overview.value}>
              <OverviewAnalytics />
            </UnderlineTabsContent>
            <UnderlineTabsContent value={DSPAnalyticsTabs.Platform.value}>
              <PlatformAnalytics />
            </UnderlineTabsContent>
            <UnderlineTabsContent value={DSPAnalyticsTabs.Album.value}>
              <AlbumAnalytics />
            </UnderlineTabsContent>
            <UnderlineTabsContent value={DSPAnalyticsTabs.Track.value}>
              <TrackAnalytics />
            </UnderlineTabsContent>
            <UnderlineTabsContent value={DSPAnalyticsTabs.Artist.value}>
              <ArtistAnalytics />
            </UnderlineTabsContent>
            <UnderlineTabsContent value={DSPAnalyticsTabs.Country.value}>
              <CountryAnalytics />
            </UnderlineTabsContent>
          </Suspense>
        </UnderlineTabs>
      </PageContainer>
    </AnalyticsMonthProvider>
  );
}

DSPAnalyticsPage.ContentPending = () => {
  return (
    <div className="flex items-center justify-center h-[calc(100vh-300px)]">
      <Loading />
    </div>
  );
};
